import { apiClient as api, resolveTenantAlias } from "./httpClient";
import type { PasswordInviteOutcome } from "./entityUserService";

// Operações do PRÓPRIO usuário (Meu perfil): foto (RN-013), link de senha para si (RN-005e) e
// currículo (RN-014). Não há id na URL — o alvo é sempre quem está autenticado.

export interface MyContact {
  email: string;
  /** DDI só com dígitos; nulo = Brasil (55) */
  phoneDdi: string | null;
  phoneCode: string | null;
  phoneNumber: string | null;
  fullName: string | null;
  professionalTypeName: string | null;
  /** Todos os perfis do vínculo na entidade corrente. */
  profileNames: string[];
}

export type CurriculumKind = "formacao" | "experiencia" | "certificacao" | "registro";

export interface CurriculumEntry {
  id: string;
  kind: CurriculumKind;
  title: string;
  institution: string | null;
  /** yyyy-MM-dd — só data, sem fuso. */
  startedAt: string | null;
  endedAt: string | null;
  current: boolean;
  description: string | null;
}

export interface CurriculumEntryPayload {
  kind: CurriculumKind;
  title: string;
  institution: string | null;
  startedAt: string | null;
  endedAt: string | null;
  current: boolean;
  description: string | null;
}

export const usersService = {
  /** Foto como blob (a API exige auth; <img src> não manda o token). Nulo = sem foto. */
  getMyPhoto: async (thumb = false): Promise<Blob | null> => {
    try {
      const response = await api.get(`/users/me/photo${thumb ? "?size=thumb" : ""}`, { responseType: "blob" });
      return response.data as Blob;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  setMyPhoto: async (blob: Blob): Promise<void> => {
    const form = new FormData();
    form.append("file", blob, "photo.jpg");
    // Content-Type nulo: o navegador monta o boundary do multipart; fixar dá 415.
    await api.post(`/users/me/photo`, form, { headers: { "Content-Type": null } });
  },

  removeMyPhoto: async (): Promise<void> => {
    await api.delete(`/users/me/photo`);
  },

  getMyContact: async (): Promise<MyContact> => {
    const response = await api.get(`/users/me/contact`);
    return response.data;
  },

  /** Link de definição de senha para si (RESET, 2 horas), por email e/ou WhatsApp. */
  sendMyPasswordLink: async (channels: { sendEmail: boolean; sendWhatsApp: boolean }): Promise<PasswordInviteOutcome> => {
    const response = await api.post(`/users/me/password-link`, {
      clinicAlias: resolveTenantAlias(),
      sendEmail: channels.sendEmail,
      sendWhatsApp: channels.sendWhatsApp,
    });
    return {
      inviteLink: response.data?.inviteLink ?? null,
      emailSent: Boolean(response.data?.emailSent),
      whatsappSent: Boolean(response.data?.whatsappSent),
      whatsappError: response.data?.whatsappError ?? null,
    };
  },

  getMyCurriculum: async (): Promise<CurriculumEntry[]> => {
    const response = await api.get(`/users/me/curriculum`);
    return response.data;
  },

  createCurriculumEntry: async (payload: CurriculumEntryPayload): Promise<string> => {
    const response = await api.post(`/users/me/curriculum`, payload);
    return response.data.id;
  },

  updateCurriculumEntry: async (id: string, payload: CurriculumEntryPayload): Promise<void> => {
    await api.put(`/users/me/curriculum/${id}`, payload);
  },

  deleteCurriculumEntry: async (id: string): Promise<void> => {
    await api.delete(`/users/me/curriculum/${id}`);
  },
};
